import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown, ArrowUpRight } from "lucide-react";
import { MEGA_NAV } from "../../data/nav";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false); // mobile
  const [activeMenu, setActiveMenu] = useState(null); // desktop mega
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();
  const timeoutRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setActiveMenu(null);
    setMobileExpanded(null);
  }, [location.pathname]);

  const handleEnter = (key) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveMenu(key);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveMenu(null), 120);
  };

  const headerCls = scrolled || activeMenu
    ? "bg-white/90 backdrop-blur-xl border-b border-violet-50 shadow-sm"
    : "bg-transparent border-b border-transparent";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${headerCls}`}
      data-testid="site-navbar"
      onMouseLeave={handleLeave}
    >
      <div className="container flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center"
          data-testid="nav-logo-link"
          aria-label="Lumotis — home"
        >
          <img
            src="/lumotis-logo.png"
            alt="Lumotis"
            width="1119"
            height="246"
            className="h-11 md:h-14 w-auto object-contain"
          />
        </Link>

        {/* Desktop mega */}
        <nav
          className="hidden lg:flex items-center gap-1"
          aria-label="Main Navigation"
        >
          {MEGA_NAV.map((menu) => (
            <div
              key={menu.key}
              className="relative"
              onMouseEnter={() => handleEnter(menu.key)}
            >
              <button
                type="button"
                onClick={() =>
                  setActiveMenu(activeMenu === menu.key ? null : menu.key)
                }
                data-testid={`nav-menu-${menu.key}`}
                aria-expanded={activeMenu === menu.key}
                className={`px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                  activeMenu === menu.key
                    ? "text-electric"
                    : "text-zinc-700 hover:text-charcoal"
                }`}
              >
                {menu.label}
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${
                    activeMenu === menu.key ? "rotate-180 text-electric" : ""
                  }`}
                />
              </button>
            </div>
          ))}
          <Link
            to="/pricing"
            data-testid="nav-link-pricing"
            className="px-4 py-2 text-sm font-medium text-zinc-700 hover:text-charcoal"
          >
            Pricing
          </Link>
          <Link
            to="/contact"
            data-testid="nav-link-contact"
            className="px-4 py-2 text-sm font-medium text-zinc-700 hover:text-charcoal"
          >
            Contact
          </Link>
        </nav>

        {/* CTA */}
        <div className="flex items-center gap-2">
          <Link
            to="/seo-audit"
            data-testid="nav-cta-audit"
            aria-label="Get a free marketing audit"
            className="hidden sm:inline-flex items-center gap-2 bg-electric text-white px-4 py-2.5 text-sm font-semibold hover:bg-electric-400 hover:-translate-y-0.5 transition-all shadow-md rounded-xl"
          >
            Get Free Audit
            <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden p-2 text-charcoal"
            aria-label="Toggle navigation"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Desktop mega panel */}
      {activeMenu && (
        <div
          className="hidden lg:block absolute inset-x-0 top-full border-t border-zinc-200 bg-white shadow-[0_20px_40px_-20px_rgba(45,45,45,0.25)]"
          onMouseEnter={() => handleEnter(activeMenu)}
          data-testid={`mega-panel-${activeMenu}`}
        >
          <div className="container py-8 md:py-10 grid grid-cols-12 gap-8">
            {(() => {
              const menu = MEGA_NAV.find((m) => m.key === activeMenu);
              return (
                <>
                  <div className="col-span-3">
                    <p className="text-overline text-electric mb-3">
                      {menu.label}
                    </p>
                    <p className="font-display text-2xl font-bold text-charcoal leading-tight">
                      {menu.key === "services" &&
                        "The full digital marketing stack, under one roof."}
                      {menu.key === "resources" &&
                        "Playbooks, guides and case studies from real client campaigns."}
                      {menu.key === "company" &&
                        "A growth partner that treats your revenue targets like our own."}
                    </p>
                    <Link
                      to={
                        menu.key === "services"
                          ? "/#services"
                          : menu.key === "resources"
                            ? "/blog"
                            : "/about"
                      }
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-electric hover:text-charcoal"
                    >
                      Learn more <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </div>
                  <div
                    className={`col-span-9 grid gap-8 ${
                      menu.columns.length >= 3 ? "grid-cols-3" : "grid-cols-2"
                    }`}
                  >
                    {menu.columns.map((col) => (
                      <div key={col.title}>
                        <p className="text-overline text-zinc-500 mb-4">
                          {col.title}
                        </p>
                        <ul className="grid gap-1">
                          {col.items.map((it) => {
                            const Icon = it.icon;
                            return (
                              <li key={it.href}>
                                <Link
                                  to={it.href}
                                  data-testid={`mega-link-${it.href.replace(/[/#]+/g, "-").replace(/^-/, "")}`}
                                  className="group flex items-start gap-3 p-3 -mx-3 hover:bg-zinc-50 transition-colors"
                                >
                                  <span className="w-9 h-9 grid place-items-center rounded-xl bg-electric/8 border border-electric/15 group-hover:bg-electric group-hover:border-electric group-hover:text-white flex-shrink-0">
                                    <Icon className="w-4 h-4" strokeWidth={2.2} />
                                  </span>
                                  <span>
                                    <span className="block text-sm font-semibold text-charcoal group-hover:text-electric">
                                      {it.label}
                                    </span>
                                    <span className="block text-xs text-zinc-500 mt-0.5">
                                      {it.desc}
                                    </span>
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden bg-white border-t border-zinc-200 max-h-[calc(100vh-4rem)] overflow-y-auto"
          data-testid="nav-mobile-panel"
        >
          <nav className="container py-5 flex flex-col" aria-label="Mobile">
            {MEGA_NAV.map((menu) => {
              const expanded = mobileExpanded === menu.key;
              return (
                <div key={menu.key} className="border-b border-zinc-100">
                  <button
                    onClick={() =>
                      setMobileExpanded(expanded ? null : menu.key)
                    }
                    className="w-full py-3 flex items-center justify-between text-base font-semibold text-charcoal"
                    data-testid={`nav-mobile-menu-${menu.key}`}
                    aria-expanded={expanded}
                  >
                    {menu.label}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
                    />
                  </button>
                  {expanded && (
                    <ul className="pb-3 pl-2 space-y-2">
                      {menu.columns.flatMap((c) => c.items).map((it) => (
                        <li key={it.href}>
                          <Link
                            to={it.href}
                            className="block py-2 text-sm text-zinc-700 hover:text-electric"
                            data-testid={`nav-mobile-link-${it.href.replace(/[/#]+/g, "-").replace(/^-/, "")}`}
                          >
                            {it.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
            <Link
              to="/pricing"
              className="py-3 text-base font-semibold text-charcoal border-b border-zinc-100"
              data-testid="nav-mobile-pricing"
            >
              Pricing
            </Link>
            <Link
              to="/contact"
              className="py-3 text-base font-semibold text-charcoal border-b border-zinc-100"
              data-testid="nav-mobile-contact"
            >
              Contact
            </Link>
            <Link
              to="/seo-audit"
              onClick={() => setOpen(false)}
              className="mt-5 inline-flex items-center justify-center gap-2 bg-electric text-white px-4 py-3 text-sm font-semibold"
              data-testid="nav-mobile-cta"
            >
              Get Free Audit <ArrowRight className="w-4 h-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
